import {
    createContext,
    Dispatch,
    MutableRefObject,
    ReactNode,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useRouter } from 'next/router';
import {
    DefaultEdgeOptions,
    Edge,
    Node,
    OnEdgesChange,
    OnNodesChange,
    useEdgesState,
    useReactFlow,
    Viewport,
    XYPosition,
} from 'reactflow';

import useApi from '@hooks/useApi';
import useCanvasEdges, { CustomEdge } from '@hooks/useCanvasEdges';
import useCanvasNodes, { UpdateNodeDataType } from '@hooks/useCanvasNodes';
import { DEFAULT_ZOOM_LEVEL } from '@hooks/useCanvasSettings';
import useEmbeddedExperiments from '@hooks/useEmbeddedExperiments';
import useInitializeCanvas from '@hooks/useInitializeCanvas';
import useLinkedExperiments from '@hooks/useLinkedExperiments';

import { CanvasResponse } from '@models/Canvas';
import { EmbeddedExperiment, SourceExperiment } from '@models/EmbeddedExperiment';
import Experiment from '@models/Experiment';
import { LinkedExperiment } from '@models/LinkedExperiment';
import Plot from '@models/Plot';

import { ApiError } from '@services/ApiError';
import { withRetry } from '@services/ApiFetcher';
import Endpoints from '@services/Endpoints';

import { mergeCanvasData } from '@util/canvasCollaboration';
import { buildParentMap, collectDescendantNodeIdsFromMap, createLookupMap } from '@util/CanvasUtil';
import Logger from '@util/Logger';

import { StateSetter } from './ContextTypes';
import { useWebSocket } from './ExperimentsWebSocketProvider';
import { ViewersProvider } from './ViewersContext';

type FocusedInputRefObject = { id: string } | null;
export type FocusedInputRefType = MutableRefObject<FocusedInputRefObject>;

export type LinkedExperimentData = {
    linked_experiment_id: string;
    source_experiment_id: string;
};

type SavePayloadType = {
    nodes?: Node[];
    edges?: Edge<CustomEdge[]>[];
    deleted_nodes?: Node[];
    deleted_edges?: Edge<CustomEdge[]>[];
    version_id?: string;
};

export type ContextType = {
    deletedNodes: Node[];
    deletedEdges: Edge<CustomEdge[]>[];
    canvasLoaded: boolean;
    canvasLoading: boolean;
    canvasNodes: Node[];
    canvasNodesError: string;
    chatWindowNodeByRunUuid: Record<string, Node>;
    chatWindowsBySource: Record<string, Node[]>;
    clearPanelChildNodes: (node: Node) => void;
    defaultEdgeOptions: DefaultEdgeOptions;
    defaultViewport: Viewport | undefined;
    deleteEmbeddedExperimentPlotNodes: (experimentId: string) => void;
    deleteModalOpen: 'node' | 'edge' | '';
    edges: Edge<CustomEdge[]>[];
    embeddedExperimentMap: Map<string, EmbeddedExperiment>;
    embeddedExperiments: EmbeddedExperiment[] | null;
    embeddedExperimentsError: string;
    embeddedExperimentsLoading: boolean;
    fetchCanvas: (skipLoadingState?: boolean) => void;
    fetchEmbeddedExperiments: () => void;
    fetchLinkedExperiments: () => void;
    focusedInputRef: FocusedInputRefType;
    handleDeleteElement: (deleteType: 'node' | 'edge' | '') => void;
    handleSelectTextNodeToEdit: (id: string) => void;
    handleUpdateEdge: (edge: CustomEdge) => void;
    nodesById: Record<string, Node>;
    plotNodeByParent: Record<string, Node[]>;
    updateNodeData: UpdateNodeDataType;
    updateNode: (
        nodeId: string,
        nodePatch: Partial<Omit<Node, 'id'>> & { data?: Partial<Node['data']> },
        options?: { skipSelectNode?: boolean },
    ) => void;
    helperLineHorizontal: number | undefined;
    helperLineVertical: number | undefined;
    isCanvasInUrl: boolean;
    isFromExperimentCard: boolean;
    linkedExperimentMap: Map<string, LinkedExperiment>;
    linkedExperiments: LinkedExperiment[] | null;
    linkedExperimentsLoading: boolean;
    linkEmbeddedExperiments: (embeddedExperimentIds: string[]) => void;
    newNodeType: string | null;
    onDragOver: (_: React.DragEvent) => void;
    onDrop: (_: React.DragEvent) => void;
    onEdgesChange: OnEdgesChange;
    onExpandAiAnalysisChatWindow: (id: string) => void;
    onNodeDrag: (_: React.MouseEvent, node: Node) => void;
    onNodeDragStop: (_: React.MouseEvent, node: Node) => void;
    onNodesChange: OnNodesChange;
    onSelectPISuggestedAnalysesNode: (id: string) => void;
    onSelectTargetAnalysisNode: (id: string) => void;
    postNewNode: (nodeData: Node['data']) => Promise<Node>;
    saveNodes: (nodes: Node[]) => void;
    selectedAiAnalysisChatWindow: Node | null;
    selectedEdge: CustomEdge | null;
    selectedLinkedExperimentData: LinkedExperimentData | null;
    selectedNode: Node | null;
    selectedPISuggestedAnalysesNode: Node | null;
    selectedTargetAnalysisNode: Node | null;
    setActivePlot: StateSetter<Plot | null>;
    setCanvasNodes: StateSetter<Node[]>;
    setDeleteModalOpen: StateSetter<'node' | 'edge' | ''>;
    setEdges: Dispatch<SetStateAction<Edge<CustomEdge[]>[]>>;
    setIsFromExperimentCard: Dispatch<SetStateAction<boolean>>;
    setNewNodeLocation: StateSetter<XYPosition | null>;
    setNewNodeType: Dispatch<SetStateAction<string | null>>;
    setSelectedAiAnalysisChatWindow: StateSetter<Node | null>;
    setSelectedEdge: Dispatch<SetStateAction<CustomEdge | null>>;
    setSelectedLinkedExperimentData: Dispatch<SetStateAction<LinkedExperimentData | null>>;
    setSelectedNode: Dispatch<SetStateAction<Node | null>>;
    setSelectedPISuggestedAnalysesNode: StateSetter<Node | null>;
    setSelectedTargetAnalysisNode: StateSetter<Node | null>;
    setShowEmbedAnalysis: Dispatch<SetStateAction<boolean>>;
    setShowExistingAnalyses: Dispatch<SetStateAction<boolean>>;
    setTextNodeToEdit: StateSetter<Node | null>;
    showEmbedAnalysis: boolean;
    showExistingAnalyses: boolean;
    sourceExperiment: SourceExperiment | null;
    textNodeToEdit: Node | null;
    unlinkAllEmbeddedExperiments: () => void;
    unlinkEmbeddedExperiments: (experimentIdsToUnlink: string[]) => void;
    updateEmbeddedExperiment: (payload: any) => void;
    updateLinkedExperiment: (payload: any) => void;
    versionId?: string;
};
// Initialize with empty functions and default values to avoid null checks
const ExperimentCanvasContext = createContext<ContextType>({} as ContextType);

export const useExperimentCanvasContext = () => {
    const context = useContext(ExperimentCanvasContext);
    if (!context) {
        throw new Error(
            'ExperimentCanvasContext has not been defined. Ensure you have wrapped your component in a context provider',
        );
    }
    return context;
};

export const useOptionalExperimentCanvasContext = (): Partial<ContextType> => {
    const context = useContext(ExperimentCanvasContext);
    if (!context) {
        return {};
    }
    return context;
};

ExperimentCanvasContext.displayName = 'ExperimentCanvasContext';

type WebSocketUpdateData = {
    object_uuid?: string;
};

export const ExperimentCanvasContextProvider = ({
    children,
    experiment,
}: {
    children?: ReactNode;
    experiment?: Experiment | null;
}) => {
    const router = useRouter();
    const isCanvasInUrl = useMemo(() => router.asPath?.includes('canvas'), [router.asPath]);
    const { deleteElements } = useReactFlow();

    const initialViewportValue: Viewport = {
        x: 0,
        y: 0,
        zoom: DEFAULT_ZOOM_LEVEL,
    };

    /**
     * Canvas Core State
     * Manages fundamental canvas state including nodes, edges, loading states, and deleted items
     */
    const [canvasNodes, setCanvasNodes] = useState<Node[]>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<CustomEdge[]>[]>([]);
    const [defaultViewport, setDefaultViewport] = useState<Viewport>(initialViewportValue);
    const [versionId, setVersionId] = useState<string | undefined>(undefined);
    const [deletedNodes, setDeletedNodes] = useState<Node[]>([]);
    const [deletedEdges, setDeletedEdges] = useState<Edge[]>([]);
    const [canvasNodesError, setCanvasNodesError] = useState<string>('');
    const [canvasLoading, setCanvasLoading] = useState(false);
    const [canvasLoaded, setCanvasLoaded] = useState(false);

    // Initialize canvas from API
    const { fetchCanvas } = useInitializeCanvas({
        experiment_id: experiment?.uuid,
        isCanvasInUrl,
        onLoadComplete: useCallback(
            (data) => {
                setCanvasNodes(data.nodes);
                setEdges(data.edges);
                setDefaultViewport(data.defaultViewport);
                setVersionId(data.versionId);
                latestVersionRef.current = data.versionId;
                setDeletedNodes(data.deletedNodes);
                setDeletedEdges(data.deletedEdges);
                setCanvasLoaded(true);
                setCanvasLoading(false);
            },
            [setCanvasNodes, setEdges, setDefaultViewport, setVersionId, setDeletedNodes, setDeletedEdges],
        ),
    });

    /**
     * Selection & UI State
     * Manages user interface state including selections, modals, and temporary UI elements
     */
    const [deleteModalOpen, setDeleteModalOpen] = useState<'node' | 'edge' | ''>('');
    const [showExistingAnalyses, setShowExistingAnalyses] = useState<boolean>(true);
    const [showEmbedAnalysis, setShowEmbedAnalysis] = useState<boolean>(false);
    const [isFromExperimentCard, setIsFromExperimentCard] = useState<boolean>(false);
    const focusedInputRef = useRef<FocusedInputRefObject>(null);

    // Selection state
    const [selectedEdge, setSelectedEdge] = useState<CustomEdge | null>(null);
    const [selectedNode, setSelectedNode] = useState<Node | null>(null);
    const [selectedLinkedExperimentData, setSelectedLinkedExperimentData] = useState<LinkedExperimentData | null>(null);
    const [textNodeToEdit, setTextNodeToEdit] = useState<Node | null>(null);
    const [selectedTargetAnalysisNode, setSelectedTargetAnalysisNode] = useState<Node | null>(null);
    const [selectedPISuggestedAnalysesNode, setSelectedPISuggestedAnalysesNode] = useState<Node | null>(null);
    const [selectedAiAnalysisChatWindow, setSelectedAiAnalysisChatWindow] = useState<Node | null>(null);

    // Node positioning and layout state
    const [newNodeLocation, setNewNodeLocation] = useState<XYPosition | null>(null);
    const [newNodeType, setNewNodeType] = useState<string | null>(null);
    const [helperLineHorizontal, setHelperLineHorizontal] = useState<number | undefined>(undefined);
    const [helperLineVertical, setHelperLineVertical] = useState<number | undefined>(undefined);
    const [activePlot, setActivePlot] = useState<Plot | null>(null);
    const [furthestX, setFurthestX] = useState<number>(0);

    const { defaultEdgeOptions, handleUpdateEdge } = useCanvasEdges({
        experiment_id: experiment?.uuid,
        edges,
        setEdges,
        canvasLoaded,
        focusedInputRef,
        setSelectedEdge,
    });

    // Node hashmaps
    const chatWindowNodeByRunUuid = useMemo(() => {
        return createLookupMap(canvasNodes, (node) => node.data?.runData?.uuid);
    }, [canvasNodes]);

    const nodesById = useMemo(() => {
        return createLookupMap(canvasNodes, (node) => node.id);
    }, [canvasNodes]);

    const plotNodeByParent = useMemo(() => {
        const result: Record<string, Node[]> = {};
        for (const node of canvasNodes) {
            if (node.type === 'plot' && node.parentNode) {
                if (!result[node.parentNode]) result[node.parentNode] = [];
                result[node.parentNode].push(node);
            }
        }
        return result;
    }, [canvasNodes]);

    const chatWindowsBySource = useMemo(() => {
        const map: Record<string, Node[]> = {};
        canvasNodes.forEach((n) => {
            if (n.type === 'aiAnalysisChatWindow' && n.data?.sourceNodeId) {
                if (!map[n.data.sourceNodeId]) map[n.data.sourceNodeId] = [];
                map[n.data.sourceNodeId].push(n);
            }
        });
        return map;
    }, [canvasNodes]);

    const {
        deleteEmbeddedExperimentPlotNodes,
        handleSelectTextNodeToEdit,
        onDragOver,
        onDrop,
        onExpandAiAnalysisChatWindow,
        onNodeDrag,
        onNodeDragStop,
        onNodesChange,
        onSelectPISuggestedAnalysesNode,
        onSelectTargetAnalysisNode,
        postNewNode,
        updateNode,
        updateNodeData,
    } = useCanvasNodes({
        experiment_id: experiment?.uuid,
        canvasLoaded,
        canvasNodes,
        setCanvasNodes,
        focusedInputRef,
        nodesById,
        // State management props
        newNodeLocation,
        setNewNodeLocation,
        textNodeToEdit,
        setTextNodeToEdit,
        selectedTargetAnalysisNode,
        setSelectedTargetAnalysisNode,
        selectedPISuggestedAnalysesNode,
        setSelectedPISuggestedAnalysesNode,
        selectedAiAnalysisChatWindow,
        setSelectedAiAnalysisChatWindow,
        selectedNode,
        setSelectedNode,
        canvasNodesError,
        setCanvasNodesError,
        newNodeType,
        setNewNodeType,
        helperLineHorizontal,
        setHelperLineHorizontal,
        helperLineVertical,
        setHelperLineVertical,
        activePlot,
        setActivePlot,
        furthestX,
        setFurthestX,
    });

    const {
        embeddedExperiments,
        error: embeddedExperimentsError,
        fetchEmbeddedExperiments,
        linkEmbeddedExperiments,
        loading: embeddedExperimentsLoading,
        sourceExperiment,
        unlinkAllEmbeddedExperiments,
        unlinkEmbeddedExperiments,
        updateEmbeddedExperiment,
        embeddedExperimentMap,
    } = useEmbeddedExperiments({ experimentId: experiment?.uuid, fetchCanvas });

    const {
        updateLinkedExperiment,
        linkedExperiments,
        loading: linkedExperimentsLoading,
        fetchLinkedExperiments,
        linkedExperimentMap,
    } = useLinkedExperiments({
        experimentId: experiment?.uuid,
        fetchCanvas,
    });
    const api = useApi();
    const logger = Logger.make('ExperimentCanvasContext');

    /**
     * Version and Collaboration State
     * Manages version tracking, pending changes, and collaboration data
     */
    // Version tracking refs to avoid state update delays
    const latestVersionRef = useRef<string | undefined>(versionId);
    const latestFetchedDataRef = useRef<CanvasResponse | null>(null);

    /**
     * Fetches the latest canvas data from the server, updates version refs, and state.
     * This function is wrapped with retry logic.
     */
    const fetchAndUpdateCanvasData = useCallback(async (): Promise<CanvasResponse> => {
        if (!experiment?.uuid) {
            logger.error(new Error('fetchAndUpdateCanvasData: Experiment UUID is not available'));
            throw new Error('Experiment UUID is not available');
        }

        const operation = async () => {
            const result: CanvasResponse = await api.get(Endpoints.lab.experiment.canvasFlow(experiment.uuid));
            result.deleted_nodes = result.deleted_nodes || [];
            result.deleted_edges = result.deleted_edges || [];

            latestFetchedDataRef.current = result;
            latestVersionRef.current = result.version_id;

            requestAnimationFrame(() => {
                setVersionId(result.version_id);
            });

            return result;
        };

        try {
            logger.info('Fetching and updating canvas data...');
            const response = await withRetry(operation);
            logger.info('Successfully fetched and updated canvas data.');
            return response;
        } catch (error) {
            logger.error(new Error(`fetchAndUpdateCanvasData failed after retries: ${error}`));
            throw error;
        }
    }, [api, experiment?.uuid, logger, setVersionId]);

    const handleVersionUpdate = useCallback(
        async (data?: WebSocketUpdateData) => {
            if (!experiment?.uuid) return;
            if (data?.object_uuid && data.object_uuid === latestVersionRef.current) {
                logger.info('Version update skipped, version matches current.');
                return;
            }

            try {
                const fetchedData = await fetchAndUpdateCanvasData();

                // latestVersionRef.current is now updated by fetchAndUpdateCanvasData
                // Proceed with merging logic
                const currentLocalData: CanvasResponse = {
                    ...fetchedData, // Use the freshly fetched server data as the base
                    nodes: canvasNodes, // Override with current local state
                    edges,
                    deleted_nodes: deletedNodes,
                    deleted_edges: deletedEdges,
                    // version_id will be from fetchedData, which is correct here
                };

                // Pass the freshly fetched server state and current local state to mergeCanvasData
                const mergeResult = mergeCanvasData(fetchedData, currentLocalData);

                if (mergeResult.hasChanges) {
                    setCanvasNodes(mergeResult.nodes);
                    setEdges(mergeResult.edges);
                    setDeletedNodes(mergeResult.deleted_nodes);
                    setDeletedEdges(mergeResult.deleted_edges);
                    logger.info('Canvas updated via WebSocket.');
                } else {
                    logger.info('WebSocket update received, but no changes after merge.');
                }
            } catch (error) {
                // Error is already logged by fetchAndUpdateCanvasData or its retry mechanism
                logger.error(new Error(`WebSocket update processing failed: ${error}`));
            }
        },
        [
            experiment?.uuid,
            fetchAndUpdateCanvasData,
            canvasNodes,
            edges,
            deletedNodes,
            deletedEdges,
            logger, // logger is stable but good to list if used directly
            setCanvasNodes,
            setEdges,
            setDeletedNodes,
            setDeletedEdges,
        ],
    );

    // Pending changes storage for debounced saves
    const pendingChanges = useRef<{
        nodes?: Node[];
        edges?: Edge<CustomEdge[]>[];
        timeout?: NodeJS.Timeout;
    }>({});

    /**
     * Handles saving canvas changes with version tracking and change detection.
     */
    const saveWithVersion = useCallback(
        async (payload: Omit<SavePayloadType, 'version_id'>) => {
            if (!experiment?.uuid || !canvasLoaded || focusedInputRef.current?.id) return;

            // Strip keys from the nodes and edges: `dragging`, `selected`, `resizing`
            payload.nodes = payload.nodes?.map((node) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { dragging, selected, resizing, ...rest } = node;
                return rest;
            });
            payload.edges = payload.edges?.map((edge) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { selected, ...rest } = edge;
                return rest;
            });

            // Include deleted nodes and edges in the payload
            // Also clean up any UI-specific properties from deleted nodes and edges
            if (deletedNodes.length > 0) {
                payload.deleted_nodes = deletedNodes.map((node) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { dragging, selected, resizing, ...rest } = node;
                    return rest;
                });
            }

            if (deletedEdges.length > 0) {
                payload.deleted_edges = deletedEdges.map((edge) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { selected, ...rest } = edge;
                    return rest;
                });
            }

            // Skip save if payload matches origin data
            // Only compare the shared properties (nodes, edges, deleted_nodes, deleted_edges)
            if (latestFetchedDataRef?.current) {
                const compareData: Omit<SavePayloadType, 'version_id'> = {
                    nodes: latestFetchedDataRef.current.nodes || [],
                    edges: latestFetchedDataRef.current.edges || [],
                    deleted_nodes: latestFetchedDataRef.current.deleted_nodes || [],
                    deleted_edges: latestFetchedDataRef.current.deleted_edges || [],
                };
                if (JSON.stringify(payload) === JSON.stringify(compareData)) {
                    return;
                }
            }

            try {
                const response: CanvasResponse = await api.put(Endpoints.lab.experiment.canvasFlow(experiment.uuid), {
                    ...payload,
                    version_id: latestVersionRef.current,
                });
                if (response.version_id) {
                    // Update ref immediately
                    latestFetchedDataRef.current = response;
                    latestVersionRef.current = response.version_id;
                    // Update state for React, but ensure it doesn't trigger saves
                    // Use RAF to batch state update
                    requestAnimationFrame(() => {
                        setVersionId(response.version_id);
                    });
                }
                // Typescript doesn't allow specifying ApiError here
            } catch (error: any) {
                logger.warn('Failed to save canvas changes.', error);
                // Check if error is ApiError and has the specific code for `invalid_version_id`
                if (
                    ApiError.isApiError(error) &&
                    Array.isArray(error?.body?.details) &&
                    error?.body?.details[0]?.code === 'invalid_version_id'
                ) {
                    logger.warn('Save failed due to invalid version ID. Triggering version update...');
                    // Handle version update which will fetch and merge new remote changes
                    handleVersionUpdate();
                } else {
                    throw error; // Re-throw other errors
                }
            }
        },
        [
            experiment?.uuid,
            canvasLoaded,
            focusedInputRef,
            deletedNodes, // Used in payload
            deletedEdges, // Used in payload
            api, // Used directly
            logger, // Used directly
            handleVersionUpdate, // Used to handle version mismatches
            setVersionId, // Used to update version state
        ],
    );

    // Debounced save function that bundles node and edge updates
    // Memoize scheduleSave without dependencies on saveWithVersion to prevent circular updates
    const scheduleSave = useCallback(() => {
        if (pendingChanges.current.timeout) {
            clearTimeout(pendingChanges.current.timeout);
        }

        pendingChanges.current.timeout = setTimeout(() => {
            if (!canvasLoaded || focusedInputRef.current?.id) return;

            const changes: Omit<SavePayloadType, 'version_id'> = {};
            if (pendingChanges.current.nodes) {
                changes.nodes = pendingChanges.current.nodes;
            }
            if (pendingChanges.current.edges) {
                changes.edges = pendingChanges.current.edges;
            }

            if (deletedNodes.length > 0) {
                changes.deleted_nodes = deletedNodes;
            }
            if (deletedEdges.length > 0) {
                changes.deleted_edges = deletedEdges;
            }

            if (Object.keys(changes).length > 0) {
                saveWithVersion(changes);
                pendingChanges.current = {}; // Clear pending changes after save
            }
        }, 750); // Debounce for 750ms
    }, [experiment?.uuid, canvasLoaded, focusedInputRef]);

    // Queue node and edge updates together, avoid duplicate saves
    useEffect(() => {
        if (!canvasLoaded || focusedInputRef.current?.id) return;

        // Skip this event if there is a dragging or resizing occurring
        if (canvasNodes.some((node) => node.dragging || node.resizing)) {
            return;
        }

        let needsSave = false;

        // Only cache new values if we don't have pending changes
        if (!pendingChanges.current.nodes) {
            pendingChanges.current.nodes = canvasNodes;
            needsSave = true;
        }
        if (!pendingChanges.current.edges) {
            pendingChanges.current.edges = edges;
            needsSave = true;
        }

        // Schedule one save if needed
        if (needsSave) {
            scheduleSave();
        }
    }, [canvasNodes, edges, deletedNodes, deletedEdges, canvasLoaded, scheduleSave, focusedInputRef]);

    // Save nodes function for direct node updates
    // TODO: consider using state setter directly...?
    const saveNodes = useCallback(
        (nodes: Node[]) => {
            if (!canvasLoaded || focusedInputRef.current?.id) return;

            // Update the nodes in state
            setCanvasNodes(nodes);

            // Queue the save operation
            pendingChanges.current.nodes = nodes;

            scheduleSave();
        },
        [canvasLoaded, focusedInputRef, scheduleSave, setCanvasNodes],
    );

    /**
     * Helper functions for state updates
     */
    const clearPanelChildNodes = (node: Node) => {
        const updatedPanelNode = {
            ...node,
            data: { ...node.data, plotIds: [], spannedCells: [], lastAdjustedIndex: 0, arrangement: null },
        }; // reset to default
        const newNodes = canvasNodes
            .filter((n) => n?.parentNode !== node.id)
            .map((n) => (n.id === node.id ? updatedPanelNode : n));
        setSelectedNode(updatedPanelNode);
        setCanvasNodes(newNodes);
    };

    const deleteChildNodeFromParent = (parentNodeId: string, childNodeId: string) => {
        const parentNode = nodesById[parentNodeId];
        if (parentNode) {
            const updatedPanelNode = {
                ...parentNode,
                data: {
                    ...parentNode.data,
                    ...(parentNode.data.plotIds
                        ? { plotIds: parentNode.data.plotIds.filter((id: string) => id !== childNodeId) }
                        : null),
                    ...(parentNode.data.spannedCells
                        ? { spannedCells: parentNode.data.spannedCells.filter((id: string) => id !== childNodeId) }
                        : null),
                },
            };
            setCanvasNodes((prev) => prev.map((n) => (n.id === parentNodeId ? updatedPanelNode : n)));
        }
    };

    const handleDeleteElement = (deleteType: 'node' | 'edge' | '') => {
        if (!deleteType) return;
        focusedInputRef.current = null;

        if (deleteType === 'node') {
            if (!selectedNode) return;

            const selectedId = selectedNode.id;

            // Recursively collect all descendant node IDs
            const parentMap = buildParentMap(canvasNodes);
            const allDescendantNodeIds = collectDescendantNodeIdsFromMap(parentMap, selectedId);

            // Include selected node
            const allNodeIdsToDelete = [selectedId, ...allDescendantNodeIds];

            // Get all edges connected to any of those nodes
            const edgeIdsToDelete = edges
                .filter((e: Edge) => allNodeIdsToDelete.includes(e.source) || allNodeIdsToDelete.includes(e.target))
                .map((e: Edge) => e.id);

            // Add to deleted nodes and edges tracking
            // Store the full node and edge objects, not just IDs
            const nodesToDelete = allNodeIdsToDelete.map((id) => nodesById[id]).filter(Boolean);
            const edgesToDelete = edgeIdsToDelete
                .map((id: string) => edges.find((e: Edge) => e.id === id))
                .filter((edge: Edge | undefined): edge is Edge<CustomEdge[]> => edge !== undefined);

            setDeletedNodes((prev) => {
                const newDeletedNodes = [...prev];
                nodesToDelete.forEach((node) => {
                    if (!newDeletedNodes.some((n) => n.id === node.id)) {
                        newDeletedNodes.push(node);
                    }
                });
                return newDeletedNodes;
            });

            setDeletedEdges((prev) => {
                const newDeletedEdges = [...prev];
                edgesToDelete.forEach((edge: Edge) => {
                    if (!newDeletedEdges.some((e) => e.id === edge.id)) {
                        newDeletedEdges.push(edge);
                    }
                });
                return newDeletedEdges;
            });

            // Trigger an immediate save to ensure deleted nodes/edges are sent to the server
            setTimeout(() => {
                saveWithVersion({
                    nodes: canvasNodes.filter((n) => !allNodeIdsToDelete.includes(n.id)),
                    edges: edges.filter((e: Edge) => !edgeIdsToDelete.includes(e.id)),
                    deleted_nodes: nodesToDelete,
                    deleted_edges: edgesToDelete,
                });
            }, 0);

            // Perform deletion
            deleteElements({
                nodes: allNodeIdsToDelete.map((id) => ({ id })),
                edges: edgeIdsToDelete.map((id: string) => ({ id })),
            });

            // Optionally clean up parent references
            if (selectedNode.parentNode) {
                // timeout allows deleteElements to run first
                setTimeout(() => {
                    deleteChildNodeFromParent(selectedNode.parentNode ?? '', selectedNode.id);
                    setEdges((prev: Edge[]) => prev.filter((e: Edge) => e.source !== selectedNode.parentNode));
                }, 1);
            }

            // Handle embedded experiments cleanup
            const embeddedPlotExperimentId = selectedNode.data?.experimentId;
            const numOfExperimentIdInstances = canvasNodes.reduce((acc, item) => {
                return acc + (item.data?.experimentId === embeddedPlotExperimentId ? 1 : 0);
            }, 0);
            const isLastEmbeddedFromExperiment = numOfExperimentIdInstances === 1;
            if (isLastEmbeddedFromExperiment && embeddedPlotExperimentId) {
                unlinkEmbeddedExperiments([embeddedPlotExperimentId]);
            }

            // Clean up
            setDeleteModalOpen('');
            setCanvasNodes(canvasNodes.filter((n) => !allNodeIdsToDelete.includes(n.id)));
            setSelectedNode(null);
            return;
        }

        if (!selectedEdge) return;
        // Add to deleted edges tracking - store the full edge object
        setDeletedEdges((prev) => {
            if (!prev.some((e) => e.id === selectedEdge.id)) {
                const newDeletedEdges = [...prev, selectedEdge];

                // Trigger an immediate save to ensure deleted edge is sent to the server
                setTimeout(() => {
                    saveWithVersion({
                        nodes: canvasNodes,
                        edges: edges.filter((e: Edge) => e.id !== selectedEdge.id),
                        deleted_edges: [selectedEdge],
                    });
                }, 0);

                return newDeletedEdges;
            }
            return prev;
        });

        deleteElements({ edges: [{ id: selectedEdge.id }] });
        setDeleteModalOpen('');
        setEdges((prev: Edge[]) => prev.filter((e: Edge) => e.id !== selectedEdge.id));
        setSelectedEdge(null);
        return;
    };

    useEffect(() => {
        setSelectedNode(null);
        setSelectedEdge(null);
    }, [experiment?.uuid]);

    /**
     * WebSocket subscription management
     * Handles real-time canvas updates from the server
     */
    const WS_UPDATE_DELAY = 200; // ms delay before processing WS updates
    const WS_EVENT_TYPE = 'canvasflow_update';
    const webSocket = (() => {
        try {
            return useWebSocket?.();
        } catch {
            return null;
        }
    })();

    useEffect(() => {
        if (!experiment?.uuid || !canvasLoaded || !webSocket?.subscribe) {
            return;
        }

        // Handler for incoming WebSocket messages
        const handleWebSocketMessage = (data?: WebSocketUpdateData) => {
            // Delay processing to ensure version info is up to date
            setTimeout(() => {
                handleVersionUpdate(data);
            }, WS_UPDATE_DELAY);
        };

        // Set up WebSocket subscription if available
        webSocket.subscribe(WS_EVENT_TYPE, handleWebSocketMessage);

        return () => {
            webSocket.unsubscribe?.(WS_EVENT_TYPE, handleWebSocketMessage);
        };
    }, [experiment?.uuid, canvasLoaded, webSocket, handleVersionUpdate]);

    return (
        <ViewersProvider>
            <ExperimentCanvasContext.Provider
                value={{
                    deletedNodes,
                    deletedEdges,
                    canvasLoaded,
                    canvasLoading,
                    canvasNodes,
                    canvasNodesError,
                    chatWindowNodeByRunUuid,
                    chatWindowsBySource,
                    clearPanelChildNodes,
                    defaultEdgeOptions,
                    defaultViewport,
                    deleteEmbeddedExperimentPlotNodes,
                    deleteModalOpen,
                    edges,
                    embeddedExperimentMap,
                    embeddedExperiments,
                    embeddedExperimentsError,
                    embeddedExperimentsLoading,
                    fetchCanvas,
                    fetchEmbeddedExperiments,
                    fetchLinkedExperiments,
                    focusedInputRef,
                    handleDeleteElement,
                    handleSelectTextNodeToEdit,
                    handleUpdateEdge,
                    helperLineHorizontal,
                    helperLineVertical,
                    isCanvasInUrl,
                    isFromExperimentCard,
                    linkedExperimentMap,
                    linkedExperiments,
                    linkedExperimentsLoading,
                    linkEmbeddedExperiments,
                    newNodeType,
                    nodesById,
                    onDragOver,
                    onDrop,
                    onEdgesChange,
                    onExpandAiAnalysisChatWindow,
                    onNodeDrag,
                    onNodeDragStop,
                    onNodesChange,
                    onSelectPISuggestedAnalysesNode,
                    onSelectTargetAnalysisNode,
                    plotNodeByParent,
                    postNewNode,
                    saveNodes,
                    selectedAiAnalysisChatWindow,
                    selectedEdge,
                    selectedLinkedExperimentData,
                    selectedNode,
                    selectedPISuggestedAnalysesNode,
                    selectedTargetAnalysisNode,
                    setActivePlot,
                    setCanvasNodes,
                    setDeleteModalOpen,
                    setEdges,
                    setIsFromExperimentCard,
                    setNewNodeLocation,
                    setNewNodeType,
                    setSelectedAiAnalysisChatWindow,
                    setSelectedEdge,
                    setSelectedLinkedExperimentData,
                    setSelectedNode,
                    setSelectedPISuggestedAnalysesNode,
                    setSelectedTargetAnalysisNode,
                    setShowEmbedAnalysis,
                    setShowExistingAnalyses,
                    setTextNodeToEdit,
                    showEmbedAnalysis,
                    showExistingAnalyses,
                    sourceExperiment,
                    textNodeToEdit,
                    unlinkAllEmbeddedExperiments,
                    unlinkEmbeddedExperiments,
                    updateEmbeddedExperiment,
                    updateNode,
                    updateNodeData,
                    updateLinkedExperiment,
                    versionId,
                }}
            >
                <>{children}</>
            </ExperimentCanvasContext.Provider>
        </ViewersProvider>
    );
};
